import React, { useCallback, useEffect, useRef, useState } from "react";
import { Skeleton, message, Button, Modal } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { API_URLS } from "../../../../api/apiUrls";
import { ReactComponent as Back } from "../../../../assets/svgs/back-button.svg";
import { getApi } from "../../../../api/api_instance";
import { SearchBar } from "../../../../components/Elements/Search/SearchBar";
import { ArrowUpOutlined } from "@ant-design/icons";
import { QuestionFormScope1234 } from "./questionUtils";

const EmpViewStandardForm = () => {
  const [sections, setSections] = useState([]);
  const [subsectionsBySection, setSubsectionsBySection] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [subsections, setSubsections] = useState([]);
  const [selectedSubsection, setSelectedSubsection] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [fillFrom, setFillFrom] = useState(false);
  const [expandAll, setExpandAll] = useState(false);
  const [showGoTop, setShowGoTop] = useState(false);
  const [currentSubsectionIndex, setCurrentSubsectionIndex] = useState(0);
  const [answersBySubsection, setAnswersBySubsection] = useState({});
  const [formattedAnswersBySubsection, setFormattedAnswersBySubsection] =
    useState({});

  const navigate = useNavigate();
  const location = useLocation();
  const scrollableContainerRef = useRef(null);
  const pendingSubsectionIndexRef = useRef(null);

  const {
    formName,
    formType,
    templateId,
    projectId,
    userId,
    id,
    standardFormAllotmentId,
  } = location.state || {};

  const getSubsectionStateKey = (sectionId, subsectionId) =>
    `${sectionId || ""}:::${subsectionId || ""}`;

  const currentSubsectionStateKey = getSubsectionStateKey(
    selectedSection?.sectionId,
    selectedSubsection,
  );

  const currentAnswers = answersBySubsection[currentSubsectionStateKey] || {};

  const setCurrentAnswers = useCallback(
    (updater) => {
      setAnswersBySubsection((prev) => {
        const previousValue = prev[currentSubsectionStateKey] || {};
        const nextValue =
          typeof updater === "function" ? updater(previousValue) : updater;

        return {
          ...prev,
          [currentSubsectionStateKey]: nextValue,
        };
      });
    },
    [currentSubsectionStateKey],
  );

  const setCurrentFormattedAnswers = useCallback(
    (formattedAnswers) => {
      setFormattedAnswersBySubsection((prev) => {
        const previousValue = prev[currentSubsectionStateKey];
        if (
          JSON.stringify(previousValue || []) ===
          JSON.stringify(formattedAnswers || [])
        ) {
          return prev;
        }

        return {
          ...prev,
          [currentSubsectionStateKey]: formattedAnswers,
        };
      });
    },
    [currentSubsectionStateKey],
  );

  useEffect(() => {
    fetchSections();

    const handleScroll = () => {
      setShowGoTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (selectedSection?.sectionId) {
      fetchSubsections(selectedSection.sectionId);
    }
  }, [selectedSection]);

  useEffect(() => {
    if (selectedSubsection && selectedSection?.sectionId) {
      fetchQuestions(selectedSubsection, selectedSection.sectionId);
    }
  }, [selectedSubsection, selectedSection]);

  const fetchSections = async () => {
    setLoading(true);
    try {
      const response = await getApi({
        apiUrl:
          formType === "Standard Form"
            ? API_URLS.GET_EMPLOYEE_STANDARD_FORM_SECTION(
                projectId,
                templateId,
                userId,
                id,
              )
            : API_URLS.GET_EMPLOYEE_DYNAMIC_FORM_SECTION(
                templateId,
                userId,
                id,
              ),
      });

      if (response?.success) {
        const data = (response.data || []).map(
          ({ _id, title, sectionID, mendate }) => ({
            title,
            _id,
            sectionId: sectionID,
            mendate: !!mendate,
          }),
        );

        setSections(data);
        setSelectedSection(data[0] || null);
        if (data.length > 0) {
          const subsectionEntries = await Promise.all(
            data.map(async (section) => {
              const subsectionList = await fetchSubsectionsData(
                section.sectionId,
              );
              return [section.sectionId, subsectionList];
            }),
          );

          setSubsectionsBySection(Object.fromEntries(subsectionEntries));
        }
      }
    } catch (error) {
      console.error("Error fetching sections", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubsectionsData = async (sectionId) => {
    const response = await getApi({
      apiUrl:
        formType === "Standard Form"
          ? API_URLS.GET_EMPLOYEE_STANDARD_FORM_SUBSECTION(
              projectId,
              templateId,
              userId,
              id,
              sectionId,
            )
          : API_URLS.GET_EMPLOYEE_DYNAMIC_FORM_SUBSECTION(
              templateId,
              sectionId,
            ),
    });

    return response?.data || [];
  };

  const fetchSubsections = async (sectionId) => {
    setLoading(true);
    try {
      const data = await fetchSubsectionsData(sectionId);
      setSubsections(data);
      setSubsectionsBySection((prev) => ({
        ...prev,
        [sectionId]: data,
      }));
      const pendingIndex = pendingSubsectionIndexRef.current;
      const resolvedIndex =
        pendingIndex !== null && pendingIndex >= 0 && pendingIndex < data.length
          ? pendingIndex
          : 0;

      setSelectedSubsection(data[resolvedIndex]?.id || null);
      setCurrentSubsectionIndex(resolvedIndex);
      pendingSubsectionIndexRef.current = null;
      if (data.length === 0) {
        setQuestions([]);
      }
    } catch (error) {
      console.error("Error fetching subsections", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async (subsectionId, sectionId) => {
    setLoading(true);
    try {
      const response = await getApi({
        apiUrl:
          formType === "Standard Form"
            ? API_URLS.GET_EMPLOYEE_QUESTIONS_FROM_SUBSECTION(
                subsectionId,
                projectId,
                templateId,
                userId,
                id,
              )
            : API_URLS.GET_EMPLOYEE_QUESTIONS_FROM_DYNAMIC_SUBSECTION(
                templateId,
                sectionId,
                subsectionId,
              ),
      });

      setQuestions(response?.data?.divisions || []);
    } catch (error) {
      console.error("Error fetching questions", error);
      message.error("Failed to fetch questions");
    } finally {
      setLoading(false);
    }
  };

  const handleSectionSelect = (section) => {
    pendingSubsectionIndexRef.current = 0;
    setSelectedSection(section);
    setSelectedSubsection(null);
    setCurrentSubsectionIndex(0);
  };

  const handleManualSubsectionSelect = (subsection) => {
    const index = subsections.findIndex((item) => item.id === subsection.id);
    if (index !== -1) {
      setCurrentSubsectionIndex(index);
    }
    setSelectedSubsection(subsection.id);
  };

  const scrollToTop = () => {
    if (scrollableContainerRef.current) {
      scrollableContainerRef.current.scrollTop = 0;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
    setShowGoTop(false);
  };

  const handlePrevious = () => {
    if (currentSubsectionIndex > 0) {
      const nextIndex = currentSubsectionIndex - 1;
      setCurrentSubsectionIndex(nextIndex);
      setSelectedSubsection(subsections[nextIndex]?.id || null);
      scrollToTop();
      return;
    }

    const currentSectionIndex = sections.findIndex(
      (section) => section.sectionId === selectedSection?.sectionId,
    );

    if (currentSectionIndex > 0) {
      const previousSection = sections[currentSectionIndex - 1];
      const previousSectionSubsections =
        subsectionsBySection[previousSection?.sectionId] || [];
      const previousSubsectionIndex = Math.max(
        previousSectionSubsections.length - 1,
        0,
      );

      pendingSubsectionIndexRef.current = previousSubsectionIndex;
      setSelectedSection(previousSection);
      setSelectedSubsection(null);
      setCurrentSubsectionIndex(previousSubsectionIndex);
      scrollToTop();
      return;
    }

    message.info("You are already at the first subsection");
  };

  const handleNext = () => {
    const currentSectionSubsections =
      subsectionsBySection[selectedSection?.sectionId] || subsections || [];
    const currentSectionIndex = sections.findIndex(
      (section) => section.sectionId === selectedSection?.sectionId,
    );
    const isLastSection = currentSectionIndex >= sections.length - 1;

    if (currentSubsectionIndex < currentSectionSubsections.length - 1) {
      const nextIndex = currentSubsectionIndex + 1;
      setCurrentSubsectionIndex(nextIndex);
      setSelectedSubsection(currentSectionSubsections[nextIndex]?.id || null);
      scrollToTop();
      return;
    }

    if (!isLastSection && currentSectionSubsections.length > 0) {
      const nextSection = sections[currentSectionIndex + 1];
      setSelectedSection(nextSection);
      setSelectedSubsection(null);
      setCurrentSubsectionIndex(0);
      scrollToTop();
    }
  };

  const canGoNext =
    currentSubsectionIndex <
      (subsectionsBySection[selectedSection?.sectionId] || subsections || [])
        .length -
        1 ||
    sections.findIndex(
      (section) => section.sectionId === selectedSection?.sectionId,
    ) <
      sections.length - 1;

  const handleStartStopFilling = () => {
    if (!fillFrom) {
      setFillFrom(true);
      return;
    }

    Modal.confirm({
      title: "Stop filling?",
      content:
        "If you stop filling, your unsaved answers will be erased. Do you want to continue?",
      okText: "Yes, stop filling",
      cancelText: "Cancel",
      okButtonProps: { danger: true },
      centered: true,
      onOk: () => {
        setFillFrom(false);
        setAnswersBySubsection({});
        setFormattedAnswersBySubsection({});
      },
    });
  };

  return (
    <div className="h-full px-4 sm:px-5 lg:px-[20px] relative mb-6 sm:mb-8">
      <div className="flex justify-between pb-2">
        <div className="flex items-center">
          <Back className="cursor-pointer" onClick={() => navigate(-1)} />
          <h3 className="text-[#06513D] text-lg sm:text-xl ml-2 mt-2">
            {formName}
          </h3>
        </div>
      </div>

      <div className="w-full rounded-t-[10px] shadow-[0_0_16px_0_#3BA2B91F] mb-6 sm:mb-8 bg-white">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 border border-[#3BA2B959] rounded-t-[10px] overflow-hidden p-1 max-h-[150px] overflow-y-auto">
          {sections.length > 0 ? (
            sections.map((section) => (
              <div
                key={section._id || section.sectionId}
                className={`relative h-10 px-1 sm:px-2 flex items-center justify-center text-[11px] sm:text-[13px] font-medium cursor-pointer rounded-xl mx-1 my-1 border border-[#3BA2B959] ${
                  selectedSection?.sectionId === section.sectionId
                    ? "bg-[#06513D] text-white shadow-md transform scale-105"
                    : "bg-transparent text-gray-700 hover:text-gray-900"
                }`}
                onClick={() => handleSectionSelect(section)}
              >
                <span className="text-center leading-tight break-words">
                  {section.title}
                </span>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center text-gray-500 py-4">
              No available section
            </div>
          )}
        </div>

        {selectedSection && (
          <div className="flex flex-wrap gap-2 sm:gap-3 lg:gap-5 p-2 text-[10px] sm:text-[11px] font-normal border border-[#3BA2B91C] shadow-[0_0_25.2px_0_#3BA2B91F]">
            {subsections.length > 0 ? (
              subsections.map((subsection) => {
                const isSelected = selectedSubsection === subsection.id;
                return (
                  <div
                    key={subsection.id}
                    className={`p-2 sm:p-3 rounded-xl cursor-pointer border transition-all duration-200 min-w-0 flex-shrink-0 relative ${
                      isSelected
                        ? "bg-[#E6EEEC] shadow-md border-[#06513D]"
                        : "bg-transparent text-black hover:bg-gray-100"
                    }`}
                    onClick={() => handleManualSubsectionSelect(subsection)}
                  >
                    <span className="break-words">{subsection.name}</span>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500">No subsections available</p>
            )}
          </div>
        )}
      </div>

      <div className="w-full bg-white border border-[#3BA2B959] rounded-[10px] pt-5 shadow-[0_0_16px_0_#3BA2B91F] mb-6 sm:mb-8">
        <div className="mt-4 px-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center w-full gap-4">
            <p className="h-[42px] flex items-center justify-center border border-[#06513D] bg-[#B6D4D933] rounded-md text-[#06513D] text-base font-light px-6">
              TOTAL QUESTIONS:{" "}
              {Array.isArray(questions)
                ? questions.reduce(
                    (sum, division) => sum + (division?.totalQuestions || 0),
                    0,
                  )
                : 0}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 w-full sm:w-auto sm:ml-auto">
              <SearchBar width="100%" className="w-full sm:w-[300px]" />

              <Button
                type="primary"
                style={{ background: "#06513D" }}
                className="text-[13px] font-light px-5 py-5 mb-5 hover:!bg-[#387464] "
                onClick={handleStartStopFilling}
              >
                {fillFrom ? "Stop Filling" : "Start Filling"}
              </Button>

              <Button
                type="primary"
                style={{ background: "#06513D" }}
                className="text-[13px] font-light px-5 py-5 mb-5 hover:!bg-[#387464]"
                onClick={() => setExpandAll((prev) => !prev)}
              >
                {expandAll ? "Collapse All" : "Expand All"}
              </Button>
            </div>
          </div>

          <div ref={scrollableContainerRef}>
            {loading ? (
              <div className="flex flex-col gap-4 p-4">
                <Skeleton.Button block active />
                <Skeleton.Button block active />
                <Skeleton.Button block active />
              </div>
            ) : selectedSubsection ? (
              <div className="mx-20 shadow-[0_0_16px_0_#3BA2B91F]">
                {questions.length > 0 ? (
                  <QuestionFormScope1234
                    questions={questions}
                    sectionId={selectedSection?.sectionId}
                    subSectionId={selectedSubsection}
                    sections={sections}
                    subsectionsBySection={subsectionsBySection}
                    answers={currentAnswers}
                    setAnswers={setCurrentAnswers}
                    answersBySubsection={answersBySubsection}
                    formattedAnswersBySubsection={formattedAnswersBySubsection}
                    onFormattedAnswersChange={setCurrentFormattedAnswers}
                    employeeId={userId}
                    userId={id}
                    standardFormAllotmentId={standardFormAllotmentId}
                    templateId={templateId}
                    projectId={projectId}
                    fillFrom={fillFrom}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                    canGoPrevious={
                      currentSubsectionIndex > 0 ||
                      sections.findIndex(
                        (section) =>
                          section.sectionId === selectedSection?.sectionId,
                      ) > 0
                    }
                    canGoNext={canGoNext}
                  />
                ) : (
                  <p className="text-gray-500 text-center">
                    No questions available.
                  </p>
                )}
              </div>
            ) : null}
          </div>

          {showGoTop && questions.length > 0 && (
            <Button
              type="primary"
              shape="circle"
              icon={<ArrowUpOutlined />}
              size="large"
              onClick={scrollToTop}
              style={{
                position: "absolute",
                bottom: "30px",
                right: "60px",
                background: "#06513D",
                zIndex: 1000,
                boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default EmpViewStandardForm;
